"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styles from "../../styles/PantallaDeProgramasAcademicos.v2.module.css";
import estudiantesStyles from "../../styles/EstudiantesStyles/Estudiantes.module.css";
import cStyles from "../../styles/ConstanciaStyles/Constancias.module.css";
import HeaderGlobal from "../../components/HeaderGlobal";
import Sidebar from "../../components/Sidebar";
import LoaderOverlay from "../../components/LoaderOverlay";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000/api";

function ConstanciasScreen() {
  // --- Estado ---
  const [constancias, setConstancias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [queryDebounced, setQueryDebounced] = useState("");
  const [etiquetas, setEtiquetas] = useState([]);

  // Formulario
  const [formMode, setFormMode] = useState("list"); // list | create | edit
  const [editId, setEditId] = useState(null);
  const [clave, setClave] = useState("");
  const [nombre, setNombre] = useState("");
  const [redaccion, setRedaccion] = useState("");
  const [showEtiquetaModal, setShowEtiquetaModal] = useState(false);
  const [savingEtiqueta, setSavingEtiqueta] = useState(false);
  const [deletingEtiquetaId, setDeletingEtiquetaId] = useState(null);
  const [etiquetaAlias, setEtiquetaAlias] = useState("");
  const [etiquetaTablaOrigen, setEtiquetaTablaOrigen] = useState("esc_estudiantes");
  const [etiquetaCampoOrigen, setEtiquetaCampoOrigen] = useState("");
  const [etiquetaTipoDato, setEtiquetaTipoDato] = useState("texto");
  const [etiquetaFormatoFecha, setEtiquetaFormatoFecha] = useState("dia_mes_anio_texto");
  const [etiquetaUsarFechaActual, setEtiquetaUsarFechaActual] = useState(false);
  const [etiquetaDescripcion, setEtiquetaDescripcion] = useState("");
  const [etiquetaEsManual, setEtiquetaEsManual] = useState(false);
  const [selectedCampoMeta, setSelectedCampoMeta] = useState(null);
  const [camposTabla, setCamposTabla] = useState([]);
  const [camposTablaDetalle, setCamposTablaDetalle] = useState([]);
  const [showCamposTablaDropdown, setShowCamposTablaDropdown] = useState(false);

  // Campus (para header)
  const [selectedCampus, setSelectedCampus] = useState(
    () => localStorage.getItem("selectedCampus") || ""
  );

  // Ref para PDF preview
  const pdfRef = useRef(null);
  const quillRef = useRef(null);
  const lastSelectionRef = useRef(null);

  // --- Fetch constancias ---
  const fetchConstancias = useCallback(async (search = "") => {
    setLoading(true);
    try {
      const url = search
        ? `${API_BASE}/escolar/constancias/?search=${encodeURIComponent(search)}`
        : `${API_BASE}/escolar/constancias/`;
      const res = await fetch(url);
      const data = await res.json();
      setConstancias(Array.isArray(data) ? data : data.results || []);
    } catch {
      setConstancias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConstancias(queryDebounced);
  }, [queryDebounced, fetchConstancias]);

  // Debounce
  useEffect(() => {
    const handler = setTimeout(() => setQueryDebounced(query), 400);
    return () => clearTimeout(handler);
  }, [query]);

  const fetchEtiquetas = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/escolar/etiquetas/`);
      if (!res.ok) {
        setEtiquetas([]);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.results || [];
      setEtiquetas(list.filter((item) => item?.activo !== false));
    } catch {
      setEtiquetas([]);
    }
  }, []);

  useEffect(() => {
    if (formMode === "create" || formMode === "edit") {
      fetchEtiquetas();
    }
  }, [formMode, fetchEtiquetas]);

  // --- Handlers ---
  const resetForm = () => {
    setClave("");
    setNombre("");
    setRedaccion("");
    setEditId(null);
    setShowEtiquetaModal(false);
    setFormMode("list");
  };

  const resetEtiquetaForm = () => {
    setEtiquetaAlias("");
    setEtiquetaTablaOrigen("esc_estudiantes");
    setEtiquetaCampoOrigen("");
    setEtiquetaTipoDato("texto");
    setEtiquetaFormatoFecha("dia_mes_anio_texto");
    setEtiquetaUsarFechaActual(false);
    setEtiquetaDescripcion("");
    setEtiquetaEsManual(false);
    setSelectedCampoMeta(null);
    setCamposTablaDetalle([]);
  };

  const openEtiquetaModal = () => {
    resetEtiquetaForm();
    setShowEtiquetaModal(true);
  };

  const closeEtiquetaModal = () => {
    setShowEtiquetaModal(false);
    setShowCamposTablaDropdown(false);
  };

  const fetchCamposTabla = useCallback(async (tabla) => {
    if (!tabla?.trim()) {
      setCamposTabla([]);
      setCamposTablaDetalle([]);
      setSelectedCampoMeta(null);
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE}/escolar/campos-tabla/?tabla=${encodeURIComponent(tabla.trim())}`
      );
      if (!res.ok) {
        setCamposTabla([]);
        setCamposTablaDetalle([]);
        setSelectedCampoMeta(null);
        return;
      }
      const data = await res.json();
      const detalle = Array.isArray(data?.campos_detalle) ? data.campos_detalle : [];
      setCamposTablaDetalle(detalle);
      setCamposTabla(
        detalle.length > 0
          ? detalle.map((item) => item?.nombre).filter(Boolean)
          : Array.isArray(data?.campos)
            ? data.campos
            : []
      );
    } catch {
      setCamposTabla([]);
      setCamposTablaDetalle([]);
      setSelectedCampoMeta(null);
    }
  }, []);

  useEffect(() => {
    if (showEtiquetaModal) {
      fetchCamposTabla(etiquetaTablaOrigen);
    }
  }, [showEtiquetaModal, etiquetaTablaOrigen, fetchCamposTabla]);

  const filteredCamposTabla = camposTabla.filter((campo) =>
    (campo || "").toLowerCase().includes(etiquetaCampoOrigen.toLowerCase())
  );

  const asignarTipoDatoPorCampo = useCallback(
    (campo) => {
      const found = camposTablaDetalle.find((item) => item?.nombre === campo);
      const tipoDato = found?.tipo_dato || "texto";
      setEtiquetaTipoDato(tipoDato);
      if (tipoDato !== "fecha") {
        setEtiquetaFormatoFecha("dia_mes_anio_texto");
      }
      setSelectedCampoMeta(found || null);
    },
    [camposTablaDetalle]
  );

  const handleNew = () => {
    resetForm();
    setFormMode("create");
  };

  const handleEdit = (c) => {
    setClave(c.clave_constancia);
    setNombre(c.nombre_constancia);
    setRedaccion(c.redaccion);
    setEditId(c.id_constancia);
    setFormMode("edit");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta constancia?")) return;
    try {
      await fetch(`${API_BASE}/escolar/constancias/${id}/`, { method: "DELETE" });
      fetchConstancias(queryDebounced);
    } catch {
      alert("Error al eliminar la constancia");
    }
  };

  const handleSave = async () => {
    const redaccionTexto = (() => {
      const temp = document.createElement("div");
      temp.innerHTML = redaccion || "";
      return (temp.textContent || temp.innerText || "").trim();
    })();

    if (!clave.trim() || !nombre.trim() || !redaccionTexto) {
      alert("Todos los campos son obligatorios.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        clave_constancia: clave.trim(),
        nombre_constancia: nombre.trim(),
        redaccion: redaccion.trim(),
      };
      const method = formMode === "edit" ? "PUT" : "POST";
      const url =
        formMode === "edit"
          ? `${API_BASE}/escolar/constancias/${editId}/`
          : `${API_BASE}/escolar/constancias/`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        const msg = err
          ? Object.values(err).flat().join(", ")
          : "Error al guardar";
        alert(msg);
        return;
      }

      alert("Constancia guardada correctamente.");
      resetForm();
      fetchConstancias(queryDebounced);
    } catch {
      alert("Error de conexión al guardar la constancia.");
    } finally {
      setSaving(false);
    }
  };

  const slugifyToken = (str) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64) || 'campo_manual';

  const handleSaveEtiqueta = async () => {
    if (etiquetaEsManual) {
      if (!etiquetaAlias.trim()) {
        alert("El nombre de la etiqueta manual es obligatorio.");
        return;
      }
    } else {
      if (!etiquetaCampoOrigen.trim()) {
        alert("Campo origen es obligatorio.");
        return;
      }
    }

    setSavingEtiqueta(true);
    try {
      const body = etiquetaEsManual
        ? {
            nombre: etiquetaAlias.trim(),
            tabla_origen: 'manual',
            campo_origen: slugifyToken(etiquetaAlias),
            tipo_dato: etiquetaTipoDato,
            es_foranea: false,
            campo_fk: null,
            tabla_foranea: null,
            campo_foraneo: null,
            formato_fecha: etiquetaTipoDato === "fecha" ? etiquetaFormatoFecha : null,
            usar_fecha_actual: etiquetaTipoDato === "fecha" ? etiquetaUsarFechaActual : false,
            descripcion: etiquetaDescripcion.trim() || null,
          }
        : {
            nombre: (etiquetaAlias || etiquetaCampoOrigen).trim(),
            tabla_origen: etiquetaTablaOrigen.trim(),
            campo_origen: etiquetaCampoOrigen.trim(),
            tipo_dato: etiquetaTipoDato,
            es_foranea: Boolean(
              selectedCampoMeta?.es_foranea || etiquetaCampoOrigen.includes("__")
            ),
            campo_fk:
              selectedCampoMeta?.campo_fk ||
              (etiquetaCampoOrigen.includes("__") ? etiquetaCampoOrigen.split("__", 1)[0] : null),
            tabla_foranea: selectedCampoMeta?.tabla_foranea || null,
            campo_foraneo:
              selectedCampoMeta?.campo_foraneo ||
              (etiquetaCampoOrigen.includes("__") ? etiquetaCampoOrigen.split("__", 2)[1] : null),
            formato_fecha: etiquetaTipoDato === "fecha" ? etiquetaFormatoFecha : null,
            usar_fecha_actual: false,
            descripcion: etiquetaDescripcion.trim() || null,
          };

      const res = await fetch(`${API_BASE}/escolar/etiquetas/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        if (res.status === 404) {
          alert("No se encontró el endpoint /escolar/etiquetas/. Falta habilitarlo en backend.");
          return;
        }

        const err = await res.json().catch(() => null);
        const msg = err
          ? Object.values(err).flat().join(", ")
          : "No fue posible registrar la etiqueta.";
        alert(msg);
        return;
      }

      alert("Etiqueta registrada correctamente.");
      fetchEtiquetas();
      closeEtiquetaModal();
    } catch {
      alert("Error de conexión al registrar la etiqueta.");
    } finally {
      setSavingEtiqueta(false);
    }
  };

  const handleDeleteEtiqueta = async (etiqueta) => {
    if (etiqueta?.en_uso) {
      alert("No se puede eliminar la etiqueta porque ya esta en uso en una constancia.");
      return;
    }

    const id = etiqueta?.id_etiqueta;
    if (!id) {
      alert("No se pudo identificar la etiqueta a eliminar.");
      return;
    }

    const label = etiqueta?.nombre || etiqueta?.campo_origen || "esta etiqueta";
    if (!window.confirm(`¿Deseas eliminar la etiqueta "${label}"?`)) return;

    setDeletingEtiquetaId(id);
    try {
      const res = await fetch(`${API_BASE}/escolar/etiquetas/${id}/`, {
        method: "DELETE",
      });
      if (!res.ok) {
        alert("No fue posible eliminar la etiqueta.");
        return;
      }
      fetchEtiquetas();
    } catch {
      alert("Error de conexión al eliminar la etiqueta.");
    } finally {
      setDeletingEtiquetaId(null);
    }
  };

  const insertEtiquetaInRedaccion = (campoEtiqueta) => {
    const token = `{{${campoEtiqueta}}}`;
    const quill = quillRef.current?.getEditor?.();

    if (!quill) {
      setRedaccion((prev) => `${prev}${prev ? " " : ""}${token}`);
      return;
    }

    const currentRange =
      quill.getSelection() ||
      lastSelectionRef.current ||
      { index: quill.getLength(), length: 0 };

    quill.focus();
    quill.setSelection(currentRange.index, currentRange.length, "silent");
    quill.insertText(currentRange.index, token, "user");
    quill.setSelection(currentRange.index + token.length, 0, "silent");
    setRedaccion(quill.root.innerHTML);
  };

  const applyEditorCommand = (command) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const currentRange =
      quill.getSelection() ||
      lastSelectionRef.current ||
      { index: quill.getLength(), length: 0 };

    quill.focus();
    quill.setSelection(currentRange.index, currentRange.length, "silent");

    if (command === "bold") {
      const currentFormat = quill.getFormat(currentRange.index, currentRange.length || 1);
      const shouldApplyBold = !Boolean(currentFormat.bold);

      if (currentRange.length > 0) {
        quill.formatText(currentRange.index, currentRange.length, "bold", shouldApplyBold, "user");
        quill.setSelection(currentRange.index, currentRange.length, "silent");
      } else {
        quill.format("bold", shouldApplyBold, "user");
      }
    }

    if (command === "justifyLeft") {
      quill.format("align", false, "user");
    }

    if (command === "justifyCenter") {
      quill.format("align", "center", "user");
    }

    if (command === "justifyRight") {
      quill.format("align", "right", "user");
    }

    if (command === "justifyFull") {
      quill.format("align", "justify", "user");
    }

    setRedaccion(quill.root.innerHTML);
  };

  const onEditorInput = (value) => {
    setRedaccion(value);
  };

  const onEditorSelectionChange = (range) => {
    if (!range) return;
    lastSelectionRef.current = range;
  };

  const quillModules = {
    toolbar: false,
  };

  const quillFormats = ["bold", "align"];

  const htmlToRender = (value) => {
    const raw = value || "";
    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(raw);
    if (hasHtmlTags) return raw;
    return raw
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "<br />");
  };

  const alignmentIcon = (type) => {
    const commonProps = {
      width: 18,
      height: 18,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
    };

    if (type === "left") {
      return (
        <svg {...commonProps}>
          <path d="M4 6H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }

    if (type === "center") {
      return (
        <svg {...commonProps}>
          <path d="M5 6H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M5 18H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }

    if (type === "right") {
      return (
        <svg {...commonProps}>
          <path d="M4 6H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M8 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }

    return (
      <svg {...commonProps}>
        <path d="M4 6H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  };

  const handleCampoOrigenChange = (value, forceAlias = false) => {
    const prevCampo = etiquetaCampoOrigen;
    setEtiquetaCampoOrigen(value);
    if (forceAlias || !etiquetaAlias.trim() || etiquetaAlias === prevCampo) {
      setEtiquetaAlias(value);
    }

    const found = camposTablaDetalle.find((item) => item?.nombre === value);
    setSelectedCampoMeta(found || null);
  };

  // --- PDF ---

  const generatePDFCanvas = async () => {
    const el = pdfRef.current;
    if (!el) return null;
    el.style.position = "fixed";
    el.style.left = "0";
    el.style.top = "0";
    el.style.zIndex = "99999";
    el.style.opacity = "1";

    const canvas = await html2canvas(el, { scale: 2, useCORS: true });

    el.style.position = "absolute";
    el.style.left = "-9999px";
    el.style.zIndex = "";
    el.style.opacity = "";
    return canvas;
  };

  const handleDownloadPDF = async () => {
    if (!clave.trim() && !nombre.trim()) {
      alert("Completa al menos la clave y nombre de la constancia para generar el PDF.");
      return;
    }
    const canvas = await generatePDFCanvas();
    if (!canvas) return;

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "letter");
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = (canvas.height * pdfW) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfW, pdfH);
    pdf.save(`Constancia_${clave || "sin_clave"}.pdf`);
  };

  const handlePrint = async () => {
    if (!clave.trim() && !nombre.trim()) {
      alert("Completa al menos la clave y nombre de la constancia para imprimir.");
      return;
    }
    const canvas = await generatePDFCanvas();
    if (!canvas) return;

    const imgData = canvas.toDataURL("image/png");
    const printWin = window.open("", "_blank");
    if (!printWin) {
      alert("El navegador bloqueó la ventana emergente. Permite pop-ups e intenta de nuevo.");
      return;
    }
    printWin.document.write(`<!DOCTYPE html>
<html><head><title>Constancia ${clave}</title>
<style>
  @page { size: letter; margin: 0; }
  body { margin: 0; display: flex; justify-content: center; }
  img { width: 100%; height: auto; }
</style></head>
<body><img src="${imgData}" onload="window.print();window.close();" /></body></html>`);
    printWin.document.close();
  };

  // --- Render ---
  return (
    <div className={styles.mainContainer}>
      <div className={styles.layout}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <main className={styles.mainContent}>
            <HeaderGlobal
              title="Constancias"
              campus={selectedCampus}
              onCampusChange={(e) => {
                const value = e?.target?.value ?? "";
                setSelectedCampus(value);
              }}
            />
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: "relative" }}>
                <LoaderOverlay visible={loading} label="Cargando constancias..." />

                {/* Barra de búsqueda */}
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.programsSearchRow}>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar por clave o nombre..."
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setQueryDebounced(query);
                          }}
                          aria-label="Buscar constancia"
                        />
                      </div>

                      <button
                        className={styles.buscarButtonProgramas}
                        onClick={() => setQueryDebounced(query)}
                      >
                        Buscar
                      </button>

                      <button className={styles.newProgramButton} onClick={handleNew}>
                        + Nueva Constancia
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tabla */}
                <div className={cStyles.tableContainer}>
                  <table className={cStyles.table}>
                    <thead>
                      <tr>
                        <th>Clave</th>
                        <th>Nombre</th>
                        <th>Fecha Creación</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {constancias.length === 0 && !loading ? (
                        <tr>
                          <td colSpan={4} className={cStyles.emptyMessage}>
                            No se encontraron constancias.
                          </td>
                        </tr>
                      ) : (
                        constancias.map((c) => (
                          <tr key={c.id_constancia}>
                            <td>{c.clave_constancia}</td>
                            <td>{c.nombre_constancia}</td>
                            <td>
                              {c.fecha_creacion
                                ? new Date(c.fecha_creacion).toLocaleDateString("es-MX")
                                : "—"}
                            </td>
                            <td>
                              <div className={cStyles.tableActions}>
                                <button
                                  type="button"
                                  aria-label="Editar"
                                  title="Editar"
                                  className={cStyles.btnEditar}
                                  onClick={() => handleEdit(c)}
                                >
                                  <img
                                    src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true"
                                    alt="Editar"
                                    className={cStyles.actionIcon}
                                  />
                                </button>
                                <button
                                  type="button"
                                  aria-label="Eliminar"
                                  title="Eliminar"
                                  className={cStyles.btnEliminar}
                                  onClick={() => handleDelete(c.id_constancia)}
                                >
                                  <img
                                    src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true"
                                    alt="Eliminar"
                                    className={cStyles.actionIcon}
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* === Modal de formulario (crear / editar) === */}
                {(formMode === "create" || formMode === "edit") && (
                  <>
                    <div className={cStyles.modalOverlay} onClick={resetForm}>
                      <div
                        className={`${cStyles.formCard} ${cStyles.modalCard}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={cStyles.modalHeader}>
                          <h3 className={cStyles.modalTitle}>
                            {formMode === "edit" ? "Editar constancia" : "Nueva constancia"}
                          </h3>
                          <button
                            type="button"
                            className={cStyles.modalCloseButton}
                            onClick={resetForm}
                          >
                            ×
                          </button>
                        </div>

                        {/* Clave */}
                        <div className={cStyles.formGroup}>
                          <label className={cStyles.formLabel}>Clave de Constancia</label>
                          <input
                            type="text"
                            className={`${cStyles.formInput} ${cStyles.formInputShort}`}
                            value={clave}
                            onChange={(e) => setClave(e.target.value)}
                            maxLength={50}
                          />
                        </div>

                        {/* Nombre */}
                        <div className={cStyles.formGroup}>
                          <label className={cStyles.formLabel}>Nombre de Constancia</label>
                          <input
                            type="text"
                            className={cStyles.formInput}
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            maxLength={200}
                          />
                        </div>

                        {/* Redacción */}
                        <div className={cStyles.formGroup}>
                          <div className={cStyles.inlineActionsRow}>
                            <button
                              type="button"
                              className={cStyles.btnEtiqueta}
                              onClick={openEtiquetaModal}
                            >
                              + Nueva etiqueta
                            </button>
                          </div>
                          <div className={cStyles.etiquetasPanel}>
                            <div className={cStyles.etiquetasTitle}>Etiquetas disponibles</div>
                            <div className={cStyles.etiquetasChips}>
                              {etiquetas.length === 0 ? (
                                <span className={cStyles.etiquetasEmpty}>
                                  Sin etiquetas registradas.
                                </span>
                              ) : (
                                etiquetas.map((etiqueta) => (
                                  <div
                                    key={etiqueta.id_etiqueta || `${etiqueta.campo_origen}-${etiqueta.nombre}`}
                                    className={cStyles.etiquetaChipWrap}
                                  >
                                    <button
                                      type="button"
                                      className={cStyles.etiquetaChip}
                                      onClick={() => insertEtiquetaInRedaccion(etiqueta.campo_origen)}
                                      title={`Insertar {{${etiqueta.campo_origen}}}`}
                                    >
                                      {etiqueta.nombre || etiqueta.campo_origen}
                                    </button>
                                    <button
                                      type="button"
                                      className={cStyles.etiquetaChipDelete}
                                      title={etiqueta.en_uso ? "Etiqueta en uso (no se puede eliminar)" : "Eliminar etiqueta"}
                                      onClick={() => handleDeleteEtiqueta(etiqueta)}
                                      disabled={deletingEtiquetaId === etiqueta.id_etiqueta || etiqueta.en_uso}
                                    >
                                      {deletingEtiquetaId === etiqueta.id_etiqueta ? "..." : "×"}
                                    </button>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                          <label className={cStyles.formLabel}>Redacción</label>
                          <div className={cStyles.editorToolbar}>
                            <button
                              type="button"
                              className={cStyles.toolbarBtn}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => applyEditorCommand("bold")}
                              aria-label="Negrita"
                              title="Negrita"
                            >
                              <span className={cStyles.boldIcon}>B</span>
                            </button>
                            <button
                              type="button"
                              className={cStyles.toolbarBtn}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => applyEditorCommand("justifyLeft")}
                              aria-label="Alinear a la izquierda"
                              title="Alinear a la izquierda"
                            >
                              {alignmentIcon("left")}
                            </button>
                            <button
                              type="button"
                              className={cStyles.toolbarBtn}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => applyEditorCommand("justifyCenter")}
                              aria-label="Centrar"
                              title="Centrar"
                            >
                              {alignmentIcon("center")}
                            </button>
                            <button
                              type="button"
                              className={cStyles.toolbarBtn}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => applyEditorCommand("justifyRight")}
                              aria-label="Alinear a la derecha"
                              title="Alinear a la derecha"
                            >
                              {alignmentIcon("right")}
                            </button>
                            <button
                              type="button"
                              className={cStyles.toolbarBtn}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => applyEditorCommand("justifyFull")}
                              aria-label="Justificar"
                              title="Justificar"
                            >
                              {alignmentIcon("justify")}
                            </button>
                          </div>
                          <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={htmlToRender(redaccion)}
                            onChange={onEditorInput}
                            onChangeSelection={onEditorSelectionChange}
                            modules={quillModules}
                            formats={quillFormats}
                            className={cStyles.richEditorQuill}
                          />
                        </div>

                        {/* Separador */}
                        <hr className={cStyles.divider} />

                        {/* Botones */}
                        <div className={cStyles.actionsRow} style={{ marginTop: 24 }}>
                          <button
                            type="button"
                            className={cStyles.btnGuardar}
                            onClick={handleSave}
                            disabled={saving}
                          >
                            {saving ? "Guardando..." : "Guardar"}
                          </button>
                          <button
                            type="button"
                            className={cStyles.btnNuevo}
                            onClick={resetForm}
                            style={{ backgroundColor: "#898d8d" }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>

                    {showEtiquetaModal && (
                      <div
                        className={`${cStyles.modalOverlay} ${cStyles.etiquetaModalOverlay}`}
                        onClick={closeEtiquetaModal}
                      >
                        <div
                          className={`${cStyles.formCard} ${cStyles.modalCard} ${cStyles.etiquetaModalCard}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={cStyles.modalHeader}>
                            <h3 className={cStyles.modalTitle}>Nueva etiqueta</h3>
                            <button
                              type="button"
                              className={cStyles.modalCloseButton}
                              onClick={closeEtiquetaModal}
                            >
                              ×
                            </button>
                          </div>

                          {/* Toggle etiqueta manual */}
                          <div className={cStyles.formGroup} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                            <input
                              type="checkbox"
                              id="etiqueta-manual-toggle"
                              checked={etiquetaEsManual}
                              onChange={(e) => {
                                setEtiquetaEsManual(e.target.checked);
                                if (e.target.checked) {
                                  setEtiquetaTipoDato("texto");
                                  setEtiquetaFormatoFecha("dia_mes_anio_texto");
                                  setEtiquetaUsarFechaActual(false);
                                  setEtiquetaCampoOrigen("");
                                  setSelectedCampoMeta(null);
                                }
                              }}
                              style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                            />
                            <label
                              htmlFor="etiqueta-manual-toggle"
                              className={cStyles.formLabel}
                              style={{ margin: 0, cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: 13 }}
                            >
                              Etiqueta manual (sin campo de tabla)
                            </label>
                          </div>

                          {!etiquetaEsManual && (
                            <>
                              <div className={cStyles.formGroup}>
                                <label className={cStyles.formLabel}>Tabla origen</label>
                                <input
                                  type="text"
                                  className={cStyles.formInput}
                                  value={etiquetaTablaOrigen}
                                  onChange={(e) => {
                                    setEtiquetaTablaOrigen(e.target.value);
                                    handleCampoOrigenChange("");
                                    setEtiquetaTipoDato("texto");
                                  }}
                                  maxLength={64}
                                />
                              </div>

                              <div className={cStyles.formGroup}>
                                <label className={cStyles.formLabel}>Campo origen</label>
                                <div className={cStyles.autocompleteWrapper}>
                                  <input
                                    type="text"
                                    className={cStyles.formInput}
                                    value={etiquetaCampoOrigen}
                                    onFocus={() => setShowCamposTablaDropdown(true)}
                                    onClick={() => setShowCamposTablaDropdown(true)}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      handleCampoOrigenChange(value);
                                      asignarTipoDatoPorCampo(value);
                                      setShowCamposTablaDropdown(true);
                                    }}
                                    onBlur={() => {
                                      setTimeout(() => setShowCamposTablaDropdown(false), 150);
                                    }}
                                    placeholder="Selecciona o escribe un campo"
                                    maxLength={64}
                                  />
                                  {showCamposTablaDropdown && (
                                    <div className={cStyles.autocompleteDropdown}>
                                      {filteredCamposTabla.length > 0 ? (
                                        filteredCamposTabla.map((campo) => (
                                          <button
                                            key={campo}
                                            type="button"
                                            className={cStyles.autocompleteItem}
                                            onClick={() => {
                                              handleCampoOrigenChange(campo, true);
                                              asignarTipoDatoPorCampo(campo);
                                              setShowCamposTablaDropdown(false);
                                            }}
                                          >
                                            {campo}
                                          </button>
                                        ))
                                      ) : (
                                        <div className={cStyles.autocompleteEmpty}>
                                          No hay campos disponibles para esa tabla.
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </>
                          )}

                          <div className={cStyles.formGroup}>
                            <label className={cStyles.formLabel}>
                              {etiquetaEsManual ? "Nombre de la etiqueta" : "Alias"}
                            </label>
                            <input
                              type="text"
                              className={cStyles.formInput}
                              value={etiquetaAlias}
                              onChange={(e) => setEtiquetaAlias(e.target.value)}
                              placeholder={etiquetaEsManual ? "Ej: Describe tu opinión" : "Se autocompleta con el campo origen"}
                              maxLength={150}
                            />
                            {etiquetaEsManual && etiquetaAlias.trim() && (
                              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                                Token: <strong style={{ color: '#1f3a5f' }}>{`{{${slugifyToken(etiquetaAlias)}}}`}</strong>
                              </div>
                            )}
                          </div>

                          <div className={cStyles.formGroup}>
                            <label className={cStyles.formLabel}>Tipo de dato</label>
                            <select
                              className={cStyles.formInput}
                              value={etiquetaTipoDato}
                              onChange={(e) => {
                                const nuevoTipo = e.target.value;
                                setEtiquetaTipoDato(nuevoTipo);
                                if (nuevoTipo !== "fecha") {
                                  setEtiquetaFormatoFecha("dia_mes_anio_texto");
                                  setEtiquetaUsarFechaActual(false);
                                }
                              }}
                              disabled={!etiquetaEsManual}
                            >
                              <option value="texto">texto</option>
                              <option value="numero">numero</option>
                              <option value="fecha">fecha</option>
                              <option value="booleano">booleano</option>
                              <option value="decimal">decimal</option>
                            </select>
                            {!etiquetaEsManual && selectedCampoMeta?.es_foranea && (
                              <div className={cStyles.fkInfoText}>
                                Foranea: {selectedCampoMeta.campo_fk} {" -> "} {selectedCampoMeta.tabla_foranea}
                                {selectedCampoMeta.campo_foraneo
                                  ? `.${selectedCampoMeta.campo_foraneo}`
                                  : ""}
                              </div>
                            )}
                          </div>

                          {etiquetaTipoDato === "fecha" && (
                            <div className={cStyles.formGroup}>
                              <label className={cStyles.formLabel}>Formato de fecha</label>
                              <select
                                className={cStyles.formInput}
                                value={etiquetaFormatoFecha}
                                onChange={(e) => setEtiquetaFormatoFecha(e.target.value)}
                              >
                                <option value="dia_mes_anio_texto">29 enero 2026</option>
                                <option value="dia_mes_anio_legal">29 días del mes de enero del año 2026 (legal)</option>
                                <option value="mes_anio_mayus">ENERO 2026</option>
                                <option value="mes_anio">enero 2026</option>
                                <option value="dd_mm_yyyy">29/01/2026</option>
                                <option value="yyyy_mm_dd">2026-01-29</option>
                              </select>
                              {etiquetaEsManual && (
                                <div
                                  className={cStyles.formGroup}
                                  style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10 }}
                                >
                                  <input
                                    type="checkbox"
                                    id="etiqueta-fecha-actual-toggle"
                                    checked={etiquetaUsarFechaActual}
                                    onChange={(e) => setEtiquetaUsarFechaActual(e.target.checked)}
                                    style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                                  />
                                  <label
                                    htmlFor="etiqueta-fecha-actual-toggle"
                                    className={cStyles.formLabel}
                                    style={{ margin: 0, cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: 13 }}
                                  >
                                    Usar fecha actual al generar constancia
                                  </label>
                                </div>
                              )}
                            </div>
                          )}

                          <div className={cStyles.formGroup}>
                            <label className={cStyles.formLabel}>Descripción</label>
                            <textarea
                              className={cStyles.formTextarea}
                              value={etiquetaDescripcion}
                              onChange={(e) => setEtiquetaDescripcion(e.target.value)}
                              style={{ minHeight: 100 }}
                            />
                          </div>

                          <div className={cStyles.actionsRow}>
                            <button
                              type="button"
                              className={cStyles.btnGuardar}
                              onClick={handleSaveEtiqueta}
                              disabled={savingEtiqueta}
                            >
                              {savingEtiqueta ? "Guardando..." : "Guardar etiqueta"}
                            </button>
                            <button
                              type="button"
                              className={cStyles.btnNuevo}
                              onClick={closeEtiquetaModal}
                              style={{ backgroundColor: "#898d8d" }}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* === Elemento oculto para generar el PDF === */}
                    <div ref={pdfRef} className={cStyles.pdfPreview}>
                      <div className={cStyles.pdfHeader}>
                        <div className={cStyles.pdfHeaderTitle}>UNIVERSIDAD LUX</div>
                        <div className={cStyles.pdfHeaderSubtitle}>Constancia</div>
                      </div>

                      <div className={cStyles.pdfBody}>
                        <div className={cStyles.pdfFieldRow}>
                          <div className={cStyles.pdfFieldLabel}>CLAVE DE CONSTANCIA</div>
                          <div className={cStyles.pdfFieldValue}>{clave}</div>
                        </div>

                        <div className={cStyles.pdfFieldRow}>
                          <div className={cStyles.pdfFieldLabel}>NOMBRE DE CONSTANCIA</div>
                          <div className={cStyles.pdfFieldValue}>{nombre}</div>
                        </div>

                        <div className={cStyles.pdfRedaccionLabel}>REDACCIÓN</div>
                        <div
                          className={cStyles.pdfRedaccionBox}
                          dangerouslySetInnerHTML={{ __html: htmlToRender(redaccion) }}
                        />
                      </div>

                    </div>
                  </>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default ConstanciasScreen;
